import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { UserContext } from "./userContext";
import MessageAPI from "../axios/message";
import NotificationAPI from "../axios/notification";
import AccountAPI from "../axios/account";
import ListingAPI, { FAVORITES_CHANGED } from "../axios/listing";
import { COUNTS_CHANGED } from "../axios/events";
import { formatNumber } from "../pages/main-page/home-utils";

// new messages from other users arrive without anything happening here, so the counts are also polled
const REFRESH_MS = 60000;

export const CountsContext = createContext({ refresh: () => { } });

// one place counts unread messages and notifications, followed listings and credit;
// the header, the tab bar, the menu and the panel all read it, so they always agree
export const CountsProvider = ({ children }) => {
    const { user } = useContext(UserContext);
    const location = useLocation();
    const [counts, setCounts] = useState({});
    // only the newest request may set the counts, in case an older one answers later
    const latest = useRef(0);

    const refresh = useCallback(() => {
        const request = ++latest.current;
        if (!user) {
            setCounts({});
            return;
        }
        Promise.all([
            MessageAPI.countUnreadMessage(),
            NotificationAPI.getNotificationCount(),
            AccountAPI.getBalance(),
            // the list endpoint reports how many listings are followed in total
            ListingAPI.getFavoriteListings(1, '-date_created', 'sr'),
        ]).then(([messageRes, notificationRes, balanceRes, followingRes]) => {
            if (request !== latest.current) return;
            setCounts({
                messages: Number(messageRes.data.result) || 0,
                notifications: Number(notificationRes.data.result) || 0,
                credit: formatNumber(balanceRes.data.result),
                following: Number(followingRes.data.count) || 0,
            });
        }).catch(err => console.log(err));
    }, [user]);

    useEffect(() => {
        refresh();
    }, [refresh, location.pathname]);

    useEffect(() => {
        const interval = setInterval(refresh, REFRESH_MS);
        window.addEventListener(COUNTS_CHANGED, refresh);
        window.addEventListener(FAVORITES_CHANGED, refresh);
        window.addEventListener('focus', refresh);
        return () => {
            clearInterval(interval);
            window.removeEventListener(COUNTS_CHANGED, refresh);
            window.removeEventListener(FAVORITES_CHANGED, refresh);
            window.removeEventListener('focus', refresh);
        };
    }, [refresh]);

    return (
        <CountsContext.Provider value={{ ...counts, refresh }}>
            {children}
        </CountsContext.Provider>
    );
};

export const useCounts = () => useContext(CountsContext);
